import { HTMLAttributes, forwardRef } from 'react';
import { ConservationStatus, AchievementRarity, Difficulty } from '@/types';

type BadgeVariant = 'default' | 'conservation' | 'achievement' | 'difficulty';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  conservationStatus?: ConservationStatus;
  achievementRarity?: AchievementRarity;
  difficulty?: Difficulty;
}

const conservationStyles: Record<ConservationStatus, string> = {
  LC: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  NT: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
  VU: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  EN: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  CR: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const achievementStyles: Record<AchievementRarity, string> = {
  common: 'bg-slate-100 text-slate-700 border border-slate-300',
  uncommon: 'bg-green-100 text-green-700 border border-green-400',
  rare: 'bg-blue-100 text-blue-700 border border-blue-400',
  epic: 'bg-purple-100 text-purple-700 border border-purple-400',
  legendary: 'bg-gradient-to-r from-yellow-100 to-amber-200 text-amber-800 border border-amber-500',
};

const difficultyStyles: Record<Difficulty, string> = {
  'pup': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
  'raft-member': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  'alpha-otter': 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200',
  'otter-professor': 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 dark:from-amber-900 dark:to-orange-900 dark:text-amber-200',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', conservationStatus, achievementRarity, difficulty, className = '', children, ...props }, ref) => {
    let variantStyle = 'bg-slate-100 text-slate-700';

    if (variant === 'conservation' && conservationStatus) {
      variantStyle = conservationStyles[conservationStatus];
    } else if (variant === 'achievement' && achievementRarity) {
      variantStyle = achievementStyles[achievementRarity];
    } else if (variant === 'difficulty' && difficulty) {
      variantStyle = difficultyStyles[difficulty];
    }

    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${variantStyle}
          ${className}
        `}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
